// Teacher workspace: classes, AI credits, progress, account.
import { AI_EXERCISE_GENERATION_ENABLED, HAS_UNLIMITED_AI_CREDITS } from "../config.js";
import { loadParentCenterData } from "../data/account.js";
import {
  canonicalExerciseValues,
  dedupeCanonicalExerciseRecords,
  groupTeacherExercises,
  renderTeacherExerciseHierarchy
} from "../domain/curriculum.js";
import {
  createClassModal,
  createSchoolModal,
  generateAIExerciseModal,
  showCreditProductsModal
} from "../features/exercise-authoring.js";
import { bindLanguage, copy, languageControl, t } from "../i18n.js";
import { escapeHTML, showModal, showToast } from "../lib/dom.js";
import { logout, render } from "../router.js";
import { state } from "../state.js";
import { supabase } from "../supabase-client.js";
import { appLayout } from "../ui/layout.js";
import { bindTeacherExerciseLinks, createManualExerciseModal } from "./teacher-exercises.js";

export function teacherView() {
  const teacherClasses = state.schoolClasses.filter(item => item.teacher_name === state.account.username);
  const canonicalTeacherExercises = dedupeCanonicalExerciseRecords(state.teacherExercises, exercise => exercise.class_id || "unassigned");
  const totalAssigned = canonicalTeacherExercises.reduce((sum, exercise) => sum + Number(exercise.assigned_students || 0), 0);
  const totalCompleted = canonicalTeacherExercises.reduce((sum, exercise) => sum + Number(exercise.completed_students || 0), 0);
  const completion = totalAssigned ? Math.round(totalCompleted / totalAssigned * 100) : 0;
  const pendingExercises = canonicalTeacherExercises.reduce((sum, exercise) => sum + Number(exercise.pending_students || 0), 0);
  const emptyClasses = teacherClasses.filter(classroom =>
    !state.teacherStudents.some(student => student.class_id === classroom.class_id)
  ).length;
  const classSnapshots = teacherClasses.map(classroom => {
    const students = state.teacherStudents.filter(student => student.class_id === classroom.class_id);
    const exercises = canonicalTeacherExercises.filter(exercise => exercise.class_id === classroom.class_id);
    const assigned = exercises.reduce((sum, exercise) => sum + Number(exercise.assigned_students || 0), 0);
    const completed = exercises.reduce((sum, exercise) => sum + Number(exercise.completed_students || 0), 0);
    const classCompletion = assigned ? Math.round(completed / assigned * 100) : 0;
    return { classroom, students, exercises, assigned, completed, classCompletion };
  });
  const classesPreview = classSnapshots.length
    ? classSnapshots.slice(0, 4).map(({ classroom, students, exercises, assigned, classCompletion }) => `
      <button type="button" class="teacher-class-snapshot" data-teacher-home-go="classes">
        <span class="teacher-class-badge">${escapeHTML(classroom.class_name)}</span>
        <span class="teacher-class-copy"><small>${escapeHTML(classroom.school_name)}</small><strong>${students.length} ${state.language === "it" ? "alunni" : "students"} · ${exercises.length} ${state.language === "it" ? "attività" : "activities"}</strong></span>
        <span class="teacher-class-progress"><i><b style="width:${classCompletion}%"></b></i><small>${assigned ? `${classCompletion}%` : (state.language === "it" ? "da avviare" : "not started")}</small></span>
        <span class="chevron">›</span>
      </button>`).join("")
    : `<div class="teacher-empty-state"><span>🏫</span><div><strong>${state.language === "it" ? "Crea la tua prima classe" : "Create your first class"}</strong><p>${state.language === "it" ? "Aggiungi scuola e classe per iniziare ad assegnare attività." : "Add a school and class to start assigning activities."}</p></div></div>`;
  const primaryPriority = emptyClasses
    ? { icon: "👥", title: state.language === "it" ? "Completa le classi" : "Complete your classes", text: state.language === "it" ? `${emptyClasses} classi non hanno ancora alunni associati.` : `${emptyClasses} classes do not have students yet.`, target: "classes" }
    : pendingExercises
      ? { icon: "⏳", title: state.language === "it" ? "Controlla le attività" : "Review activities", text: state.language === "it" ? `${pendingExercises} assegnazioni devono ancora essere completate.` : `${pendingExercises} assignments still need to be completed.`, target: "progress" }
      : { icon: "✓", title: state.language === "it" ? "Tutto sotto controllo" : "Everything is on track", text: state.language === "it" ? "Non ci sono situazioni urgenti nelle tue classi." : "There are no urgent issues in your classes.", target: "progress" };

  appLayout(`
    <div class="teacher-command-dashboard">
      <section class="card teacher-command-hero">
        <div class="teacher-command-copy">
          <span class="teacher-live-status"><i></i>${state.language === "it" ? "Workspace aggiornato" : "Workspace up to date"}</span>
          <span class="eyebrow">${state.language === "it" ? "La tua giornata didattica" : "Your teaching day"}</span>
          <h2>${state.language === "it" ? `Bentornato, ${escapeHTML(state.account.username)}` : `Welcome back, ${escapeHTML(state.account.username)}`}</h2>
          <p>${state.language === "it" ? "Classi, attività e progressi sono riuniti in una vista chiara. Parti dalla priorità oppure prepara subito il prossimo lavoro." : "Classes, activities and progress are gathered in one clear view. Start with the priority or prepare the next assignment."}</p>
          <div class="teacher-command-actions">
            ${AI_EXERCISE_GENERATION_ENABLED ? `<button type="button" class="primary" data-teacher-home-go="ai">✦ ${state.language === "it" ? "Genera con AI" : "Generate with AI"}</button>` : ""}
            <button type="button" class="${AI_EXERCISE_GENERATION_ENABLED ? "secondary" : "primary"}" data-teacher-home-go="exercises">✎ ${state.language === "it" ? "Gestisci esercizi" : "Manage exercises"}</button>
          </div>
        </div>
        <div class="teacher-command-scoreboard">
          <div><span>🏫</span><strong>${teacherClasses.length}</strong><small>${state.language === "it" ? "classi attive" : "active classes"}</small></div>
          <div><span>👥</span><strong>${state.teacherStudents.length}</strong><small>${state.language === "it" ? "alunni" : "students"}</small></div>
          <div><span>✓</span><strong>${completion}%</strong><small>${state.language === "it" ? "completato" : "completed"}</small></div>
        </div>
      </section>

      <section class="teacher-command-kpis">
        <article><span>🏛️</span><div><strong>${state.teacherSchools.length}</strong><small>${state.language === "it" ? "scuole collegate" : "connected schools"}</small></div></article>
        <article><span>📚</span><div><strong>${canonicalTeacherExercises.length}</strong><small>${state.language === "it" ? "attività create" : "created activities"}</small></div></article>
        <article><span>📌</span><div><strong>${totalAssigned}</strong><small>${state.language === "it" ? "assegnazioni" : "assignments"}</small></div></article>
        ${AI_EXERCISE_GENERATION_ENABLED ? `<article class="ai"><span>✦</span><div><strong>${HAS_UNLIMITED_AI_CREDITS ? "∞" : (state.aiWallet?.balance ?? 0)}</strong><small>${state.language === "it" ? "crediti AI beta" : "beta AI credits"}</small></div></article>` : ""}
      </section>

      <div class="teacher-command-grid">
        <section class="card teacher-class-overview">
          <div class="teacher-section-heading"><div><span class="eyebrow">${state.language === "it" ? "Le tue classi" : "Your classes"}</span><h2>${state.language === "it" ? "Situazione per classe" : "Class overview"}</h2></div><button type="button" class="link-button" data-teacher-home-go="classes">${state.language === "it" ? "Vedi tutte" : "View all"} →</button></div>
          <div class="teacher-class-snapshot-list">${classesPreview}</div>
        </section>
        <aside class="teacher-command-aside">
          <section class="card teacher-priority-card ${primaryPriority.target === "classes" && emptyClasses ? "warn" : ""}">
            <span class="teacher-priority-icon">${primaryPriority.icon}</span><small>${state.language === "it" ? "Priorità suggerita" : "Suggested priority"}</small><h3>${primaryPriority.title}</h3><p>${primaryPriority.text}</p>
            <button type="button" class="secondary full" data-teacher-home-go="${primaryPriority.target}">${state.language === "it" ? "Apri dettaglio" : "Open details"} →</button>
          </section>
          <section class="card teacher-quick-plan">
            <div><span>1</span><p><strong>${state.language === "it" ? "Prepara" : "Prepare"}</strong><small>${state.language === "it" ? "Genera o crea l’attività" : "Generate or create an activity"}</small></p></div>
            <div><span>2</span><p><strong>${state.language === "it" ? "Rivedi" : "Review"}</strong><small>${state.language === "it" ? "Controlla domande e risposte" : "Check questions and answers"}</small></p></div>
            <div><span>3</span><p><strong>${state.language === "it" ? "Assegna" : "Assign"}</strong><small>${state.language === "it" ? "Invia alla classe scelta" : "Send to the selected class"}</small></p></div>
          </section>
        </aside>
      </div>
    </div>`,
    state.language === "it" ? "Panoramica" : "Overview",
    state.language === "it" ? "Quello che serve oggi, senza rumore." : "What you need today, without clutter."
  );
  document.querySelectorAll("[data-teacher-home-go]").forEach(button => button.addEventListener("click", () => {
    state.view = button.dataset.teacherHomeGo;
    render();
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }));
}

function bindTeacherClassInvites() {
  document.querySelectorAll("[data-class-invite]").forEach(button => {
    button.addEventListener("click", async () => {
      const { data, error } = await supabase.rpc("create_invitation", {
        invitation_type: "class",
        target_class_id: button.dataset.classInvite,
        valid_for: "30 days",
        allowed_uses: 100
      });
      if (error) return showToast(error.message);
      const url = `${location.origin}/class/${data}`;
      showModal(`<div class="modal-header"><h2>${state.language === "it" ? "Invito classe" : "Class invitation"}</h2><button class="close-button" data-close-modal>×</button></div><p style="color:var(--muted)">${state.language === "it" ? "Condividi questo link con gli alunni." : "Share this link with students."}</p><input value="${escapeHTML(url)}" readonly><button class="primary full" id="copy-class-link" style="margin-top:12px">${state.language === "it" ? "Copia link" : "Copy link"}</button>`);
      document.querySelector("#copy-class-link").addEventListener("click", async () => {
        await navigator.clipboard.writeText(url);
        showToast(state.language === "it" ? "Link copiato." : "Link copied.");
      });
    });
  });
}

export function teacherClassesView() {
  const teacherClasses = state.schoolClasses.filter(item => item.teacher_name === state.account.username);
  const canonicalClasses = new Map(groupTeacherExercises(state.teacherExercises).map(classroom => [classroom.id, classroom]));
  const classCards = teacherClasses.length
    ? teacherClasses.map(classroom => {
        const canonicalClass = canonicalClasses.get(classroom.class_id);
        const exercises = canonicalExerciseValues(canonicalClass);
        const assigned = exercises.reduce((sum, exercise) => sum + Number(exercise.assigned_students || 0), 0);
        const pending = exercises.reduce((sum, exercise) => sum + Number(exercise.pending_students || 0), 0);
        const completed = exercises.reduce((sum, exercise) => sum + Number(exercise.completed_students || 0), 0);
        const progress = assigned ? Math.round((completed / assigned) * 100) : 0;
        const classStudents = state.teacherStudents.filter(student => student.class_id === classroom.class_id);
        const studentList = classStudents.length
          ? classStudents.map(student => `
            <div class="exercise-card compact-student-card">
              <span class="avatar">${escapeHTML(student.nickname[0].toUpperCase())}</span>
              <span><h3>${escapeHTML(student.nickname)}</h3><p>${t("level")} ${student.level} · ${student.questions_solved} ${t("questions")}</p></span>
            </div>`).join("")
          : `<p style="color:var(--muted)">${state.language === "it" ? "Nessun alunno associato a questa classe." : "No students associated with this class."}</p>`;
        return `<section class="card teacher-class-card">
          <div class="library-heading">
            <div>
              <span class="eyebrow">${escapeHTML(classroom.school_name)}, ${escapeHTML(classroom.school_city)}</span>
              <h2>${escapeHTML(classroom.class_name)}</h2>
              <p>${escapeHTML(classroom.academic_year_name)}</p>
            </div>
            <button class="secondary" data-class-invite="${classroom.class_id}">${state.language === "it" ? "Invita alunni" : "Invite students"}</button>
          </div>
          <div class="stat-grid">
            <div class="stat"><strong>${classStudents.length}</strong><span>${state.language === "it" ? "alunni" : "students"}</span></div>
            <div class="stat"><strong>${exercises.length}</strong><span>${state.language === "it" ? "esercizi" : "exercises"}</span></div>
            <div class="stat"><strong>${progress}%</strong><span>${state.language === "it" ? "avanzamento" : "progress"}</span></div>
            <div class="stat"><strong>${pending}</strong><span>${state.language === "it" ? "in attesa" : "pending"}</span></div>
          </div>
          <details class="topic-group" ${classStudents.length ? "open" : ""}>
            <summary><strong>${state.language === "it" ? "Alunni della classe" : "Class students"}</strong><span>${classStudents.length}</span></summary>
            <div class="exercise-list">${studentList}</div>
          </details>
          <details class="topic-group" ${exercises.length ? "open" : ""}>
            <summary><strong>${state.language === "it" ? "Esercizi assegnati" : "Assigned exercises"}</strong><span>${exercises.length}</span></summary>
            <div class="teacher-class-exercise-tree">
              ${canonicalClass && exercises.length
                ? renderTeacherExerciseHierarchy(canonicalClass)
                : `<p style="color:var(--muted)">${state.language === "it" ? "Nessun esercizio assegnato a questa classe." : "No exercises assigned to this class."}</p>`}
            </div>
          </details>
        </section>`;
      }).join("")
    : `<section class="card empty-state"><div class="empty-icon">♟</div><h2>${state.language === "it" ? "Nessuna classe" : "No classes"}</h2><p>${state.language === "it" ? "Crea una classe dopo la verifica insegnante." : "Create a class after teacher verification."}</p></section>`;
  const schoolCards = state.teacherSchools.length
    ? state.teacherSchools.map(school => {
        const schoolClasses = teacherClasses.filter(classroom => classroom.school_id === school.school_id);
        const schoolStudents = state.teacherStudents.filter(student => student.school_id === school.school_id);
        return `<div class="exercise-card">
          <span class="exercise-symbol">🏫</span>
          <span><h3>${escapeHTML(school.school_name)}</h3><p>${escapeHTML(school.school_city)} · ${schoolClasses.length} ${state.language === "it" ? "classi" : "classes"} · ${schoolStudents.length} ${state.language === "it" ? "alunni" : "students"}</p></span>
        </div>`;
      }).join("")
    : `<div class="empty-state"><div class="empty-icon">🏫</div><h2>${state.language === "it" ? "Nessuna scuola" : "No schools"}</h2><p>${state.language === "it" ? "Aggiungi la prima scuola per poi creare le classi." : "Add your first school, then create classes."}</p></div>`;
  const selectedOrgTab = state.teacherOrgTab || "schools";
  const orgTabs = [
    ["schools", "🏫", state.language === "it" ? "Scuole" : "Schools"],
    ["classes", "♟", state.language === "it" ? "Classi" : "Classes"]
  ].map(([tab, icon, label]) => `
    <button class="tab-button ${selectedOrgTab === tab ? "active" : ""}" data-teacher-org-tab="${tab}">
      <span>${icon}</span> ${label}
    </button>`).join("");
  const orgContent = selectedOrgTab === "classes"
    ? `<section class="card admin-full-card">
        <div class="library-heading">
          <div><span class="eyebrow">${state.language === "it" ? "Classi" : "Classes"}</span><h2>${state.language === "it" ? "Le mie classi" : "My classes"}</h2><p>${state.language === "it" ? "Ogni classe contiene direttamente i suoi alunni e gli esercizi assegnati." : "Each class directly contains its students and assigned exercises."}</p></div>
          <button class="primary" id="create-class-web">${state.language === "it" ? "Crea classe" : "Create class"}</button>
        </div>
        <div class="exercise-list">${classCards}</div>
      </section>`
    : `<section class="card admin-full-card">
        <div class="library-heading">
          <div><span class="eyebrow">${state.language === "it" ? "Scuole" : "Schools"}</span><h2>${state.language === "it" ? "Le mie scuole" : "My schools"}</h2><p>${state.language === "it" ? "Prima definisci le scuole, poi crei le classi associate." : "Define schools first, then create linked classes."}</p></div>
          <button class="secondary" id="create-school-web">${state.language === "it" ? "Aggiungi scuola" : "Add school"}</button>
        </div>
        <div class="exercise-list">${schoolCards}</div>
      </section>`;

  appLayout(`
    <div class="admin-dashboard-grid professional-dashboard teacher-organisation-page">
      <section class="card admin-full-card workspace-context-card">
        <div class="library-heading">
          <div>
            <span class="eyebrow">${state.language === "it" ? "Organizzazione" : "Organisation"}</span>
            <h2>${state.language === "it" ? "Organizzazione didattica" : "Teaching organisation"}</h2>
            <p>${state.language === "it" ? "Scegli scuole oppure classi. Gli alunni sono visibili direttamente dentro la classe." : "Choose schools or classes. Students are shown directly inside their class."}</p>
          </div>
        </div>
        <div class="teacher-subtabs">${orgTabs}</div>
      </section>
      ${orgContent}
    </div>`,
    state.language === "it" ? "Classi e scuole" : "Classes and schools",
    state.language === "it" ? "Gestisci scuole, classi, inviti e alunni." : "Manage schools, classes, invitations and students."
  );
  document.querySelector("#create-school-web")?.addEventListener("click", createSchoolModal);
  document.querySelector("#create-class-web")?.addEventListener("click", createClassModal);
  document.querySelectorAll("[data-teacher-org-tab]").forEach(button => {
    button.addEventListener("click", () => {
      state.teacherOrgTab = button.dataset.teacherOrgTab;
      teacherClassesView();
    });
  });
  bindTeacherExerciseLinks();
  bindTeacherClassInvites();
}

export function teacherAIView() {
  const canonicalTeacherExercises = dedupeCanonicalExerciseRecords(state.teacherExercises, exercise => exercise.class_id || "unassigned");
  const canonicalCatalogExercises = dedupeCanonicalExerciseRecords(state.teacherCatalogExercises);
  appLayout(`
    <div class="admin-dashboard-grid">
      <section class="admin-hero card admin-wide-card teacher-ai-hero">
        <div>
          <span class="eyebrow">${state.language === "it" ? "Creazione esercizi" : "Exercise creation"}</span>
          <h2>${AI_EXERCISE_GENERATION_ENABLED
            ? (state.language === "it" ? "AI e creazione contenuti" : "AI and content creation")
            : (state.language === "it" ? "Crea contenuti" : "Create content")}</h2>
          <p>${AI_EXERCISE_GENERATION_ENABLED
            ? (state.language === "it" ? "Genera una bozza, controlla domande e risposte, poi conferma l’assegnazione alla classe." : "Generate a draft, review questions and answers, then confirm assignment to the class.")
            : (state.language === "it" ? "Crea l’esercizio, controlla domande e risposte, poi conferma l’assegnazione alla classe." : "Create the exercise, review questions and answers, then confirm assignment to the class.")}</p>
        </div>
        <div class="admin-hero-metrics">
          ${AI_EXERCISE_GENERATION_ENABLED ? `<div class="admin-kpi accent"><strong>${HAS_UNLIMITED_AI_CREDITS ? "∞" : (state.aiWallet?.balance ?? 0)}</strong><span>${state.language === "it" ? "crediti AI beta" : "beta AI credits"}</span></div>` : ""}
          <div class="admin-kpi"><strong>${canonicalTeacherExercises.length}</strong><span>${state.language === "it" ? "esercizi creati" : "created exercises"}</span></div>
          <div class="admin-kpi accent"><strong>${canonicalCatalogExercises.length}</strong><span>${state.language === "it" ? "catalogo" : "catalogue"}</span></div>
        </div>
      </section>
      <section class="card admin-side-card workspace-action-card">
        <h2>${state.language === "it" ? "Crea esercizi" : "Create exercises"}</h2>
        <div class="quick-actions">
          ${AI_EXERCISE_GENERATION_ENABLED ? `<button class="primary" id="generate-ai-web">${state.language === "it" ? "Genera bozza AI" : "Generate AI draft"}</button>` : ""}
          <button class="${AI_EXERCISE_GENERATION_ENABLED ? "secondary" : "primary"}" id="create-manual-ai">${state.language === "it" ? "Crea manualmente" : "Create manually"}</button>
          ${AI_EXERCISE_GENERATION_ENABLED
            ? (HAS_UNLIMITED_AI_CREDITS
                ? `<span class="status-pill">${state.language === "it" ? "Beta · AI illimitata" : "Beta · unlimited AI"}</span>`
                : `<button class="secondary" id="buy-ai-credits">${state.language === "it" ? "Acquista crediti AI" : "Buy AI credits"}</button>`)
            : ""}
        </div>
      </section>
      <section class="card admin-side-card workspace-flow-card">
        <h2>${state.language === "it" ? "Flusso corretto" : "Correct flow"}</h2>
        <div class="compact-stack">
          <div class="compact-row"><strong>1</strong><span>${AI_EXERCISE_GENERATION_ENABLED ? (state.language === "it" ? "Genera bozza" : "Generate draft") : (state.language === "it" ? "Crea l’esercizio" : "Create the exercise")}</span></div>
          <div class="compact-row"><strong>2</strong><span>${state.language === "it" ? "Rivedi e correggi" : "Review and edit"}</span></div>
          <div class="compact-row"><strong>3</strong><span>${state.language === "it" ? "Conferma assegnazione" : "Confirm assignment"}</span></div>
        </div>
      </section>
      <section class="card admin-full-card">
        <div class="library-heading">
          <div>
            <span class="eyebrow">${state.language === "it" ? "Dove trovo gli esercizi?" : "Where are exercises?"}</span>
            <h2>${state.language === "it" ? "Gli esercizi confermati sono nella pagina Esercizi" : "Confirmed exercises are in the Exercises page"}</h2>
            <p>${state.language === "it" ? "Questa pagina serve solo per creare e revisionare. Dopo la conferma, consulta assegnazioni e catalogo nella sezione Esercizi." : "This page is only for creation and review. After confirmation, check assignments and catalogue in the Exercises section."}</p>
          </div>
          <button class="secondary" data-teacher-go="exercises">${state.language === "it" ? "Apri esercizi" : "Open exercises"}</button>
        </div>
      </section>
    </div>`,
    AI_EXERCISE_GENERATION_ENABLED
      ? (state.language === "it" ? "AI e contenuti" : "AI and content")
      : (state.language === "it" ? "Crea contenuti" : "Create content"),
    AI_EXERCISE_GENERATION_ENABLED
      ? (state.language === "it" ? "Generazione, revisione e conferma assegnazione." : "Generation, review and assignment confirmation.")
      : (state.language === "it" ? "Creazione, revisione e conferma assegnazione." : "Creation, review and assignment confirmation.")
  );
  document.querySelector("#generate-ai-web")?.addEventListener("click", generateAIExerciseModal);
  document.querySelector("#create-manual-ai")?.addEventListener("click", createManualExerciseModal);
  document.querySelector("#buy-ai-credits")?.addEventListener("click", showCreditProductsModal);
  document.querySelector("[data-teacher-go='exercises']")?.addEventListener("click", () => {
    state.view = "exercises";
    render();
  });
}

export function teacherProgressView() {
  const canonicalTeacherExercises = dedupeCanonicalExerciseRecords(state.teacherExercises, exercise => exercise.class_id || "unassigned");
  const totalExercises = canonicalTeacherExercises.length;
  const assigned = canonicalTeacherExercises.reduce((sum, exercise) => sum + Number(exercise.assigned_students || 0), 0);
  const completed = canonicalTeacherExercises.reduce((sum, exercise) => sum + Number(exercise.completed_students || 0), 0);
  const completion = assigned ? Math.round(completed / assigned * 100) : 0;
  const studentRows = state.teacherStudents.length
    ? state.teacherStudents.map(student => `
      <div class="exercise-card">
        <span class="avatar">${escapeHTML(student.nickname.slice(0, 1).toUpperCase())}</span>
        <span><h3>${escapeHTML(student.nickname)}</h3><p>${escapeHTML(student.school_name)} · ${escapeHTML(student.class_name)}</p></span>
        <strong>${student.questions_solved} ${state.language === "it" ? "risposte" : "answers"}</strong>
      </div>`).join("")
    : `<div class="empty-state"><p>${state.language === "it" ? "Nessun alunno associato." : "No associated students."}</p></div>`;
  appLayout(`
    <div class="stack teacher-progress-page">
      <section class="card workspace-page-intro teacher-progress-intro"><div><span class="eyebrow">${state.language === "it" ? "Analisi didattica" : "Learning analytics"}</span><h2>${state.language === "it" ? "Un colpo d’occhio sui risultati" : "Results at a glance"}</h2><p>${state.language === "it" ? "Monitora partecipazione e completamento senza perdere di vista i singoli alunni." : "Monitor participation and completion while keeping individual students visible."}</p></div><span class="workspace-intro-symbol">↗</span></section>
      <div class="stat-grid workspace-stat-grid">
        <div class="stat"><strong>${state.teacherStudents.length}</strong><span>${state.language === "it" ? "alunni" : "students"}</span></div>
        <div class="stat"><strong>${totalExercises}</strong><span>${state.language === "it" ? "esercizi creati" : "created exercises"}</span></div>
        <div class="stat"><strong>${completion}%</strong><span>${state.language === "it" ? "completamento" : "completion"}</span></div>
      </div>
      <section class="card workspace-data-card">
        <h2>${state.language === "it" ? "Andamento degli alunni" : "Student progress"}</h2>
        <div class="progress-track" style="margin:18px 0"><span style="width:${completion}%"></span></div>
        <div class="exercise-list">${studentRows}</div>
      </section>
    </div>`,
    state.language === "it" ? "Progressi delle classi" : "Class progress",
    state.language === "it" ? "Statistiche aggregate delle classi e degli esercizi assegnati." : "Aggregate statistics for classes and assigned exercises."
  );
}

export function teacherAccountView() {
  const classes = state.schoolClasses.filter(item => item.teacher_name === state.account.username);
  appLayout(`
    <div class="admin-dashboard-grid professional-dashboard teacher-account-page">
      <section class="card admin-wide-card workspace-profile-card">
        <div>
          <span class="eyebrow">${state.language === "it" ? "Profilo insegnante" : "Teacher profile"}</span>
          <h2>@${escapeHTML(state.account.username)}</h2>
          <p style="color:var(--muted)">${state.language === "it" ? "Qui trovi solo impostazioni account. Scuole, classi e alunni sono nella pagina Classi e scuole." : "This page only contains account settings. Schools, classes and students are in Classes and schools."}</p>
        </div>
        <div class="stat-grid">
          <div class="stat"><strong>${state.teacherSchools.length}</strong><span>${state.language === "it" ? "scuole" : "schools"}</span></div>
          <div class="stat"><strong>${classes.length}</strong><span>${state.language === "it" ? "classi" : "classes"}</span></div>
          <div class="stat"><strong>${HAS_UNLIMITED_AI_CREDITS ? "∞" : (state.aiWallet?.balance ?? 0)}</strong><span>${state.language === "it" ? "crediti AI beta" : "beta AI credits"}</span></div>
        </div>
      </section>
      <aside class="card admin-side-card workspace-settings-card">
        <h2>${state.language === "it" ? "Impostazioni" : "Settings"}</h2>
        ${languageControl()}
        <button class="secondary full" data-teacher-go="classes">${state.language === "it" ? "Apri classi e scuole" : "Open classes and schools"}</button>
        ${state.roles.includes("parent") ? `<button class="secondary full" id="teacher-open-parent">🏡 ${state.language === "it" ? "Apri Centro Famiglia" : "Open Family Center"}</button>` : ""}
        <button class="primary full" id="teacher-account-logout">${t("logout")}</button>
      </aside>
    </div>`,
    state.language === "it" ? "Account insegnante" : "Teacher account",
    state.language === "it" ? "Impostazioni e organizzazione scolastica." : "Settings and school organisation."
  );
  bindLanguage();
  document.querySelector("[data-teacher-go='classes']")?.addEventListener("click", () => {
    state.view = "classes";
    render();
  });
  document.querySelector("#teacher-open-parent")?.addEventListener("click", async () => {
    state.teacherMode = false;
    state.parentMode = true;
    state.profile = null;
    state.parentTab = "today";
    sessionStorage.removeItem("brainmath-parent-child-mode");
    await loadParentCenterData();
    render();
  });
  document.querySelector("#teacher-account-logout").addEventListener("click", logout);
}
